// Document Viewer Modal - PDF viewer with chunk bounding box overlays

import { useState, useCallback, useRef, useEffect } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button, StatusBadge } from '../common';
import { adminService } from '../../services/admin';
import type { RagDocumentDetail, DocumentChunk } from '../../types/api';
import styles from './DocumentViewerModal.module.css';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Set up PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface DocumentViewerModalProps {
  document: RagDocumentDetail;
  onClose: () => void;
}

interface ChunkOverlay {
  chunk: DocumentChunk;
  pageNumber: number;
  polygon: number[];
}

export function DocumentViewerModal({ document, onClose }: DocumentViewerModalProps) {
  const [numPages, setNumPages] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [selectedChunk, setSelectedChunk] = useState<DocumentChunk | null>(null);
  const [hoveredChunk, setHoveredChunk] = useState<string | null>(null);
  const [showOverlays, setShowOverlays] = useState<boolean>(true);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pageSize, setPageSize] = useState<{ width: number; height: number } | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState<boolean>(true);
  const [expandedChunks, setExpandedChunks] = useState<Set<string>>(new Set());
  const [chunkViewMode, setChunkViewMode] = useState<Record<string, 'summary' | 'raw'>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  // Toggle chunk expansion
  const toggleChunkExpanded = (chunkId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedChunks(prev => {
      const next = new Set(prev);
      if (next.has(chunkId)) {
        next.delete(chunkId);
      } else {
        next.add(chunkId);
      }
      return next;
    });
  };

  // Toggle chunk view mode (summary vs raw)
  const setChunkView = (chunkId: string, mode: 'summary' | 'raw', e: React.MouseEvent) => {
    e.stopPropagation();
    setChunkViewMode(prev => ({ ...prev, [chunkId]: mode }));
  };

  // Fetch PDF with auth token and create blob URL
  useEffect(() => {
    let blobUrl: string | null = null;

    const fetchPdf = async () => {
      try {
        setPdfLoading(true);
        setPdfError(null);
        const blob = await adminService.getRagDocumentPdf(document.id);
        blobUrl = URL.createObjectURL(blob);
        setPdfBlobUrl(blobUrl);
      } catch (error) {
        setPdfError(`Failed to load PDF: ${error instanceof Error ? error.message : 'Unknown error'}`);
      } finally {
        setPdfLoading(false);
      }
    };

    fetchPdf();

    // Cleanup blob URL on unmount
    return () => {
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [document.id]);

  // Get all chunk overlays for the current page
  const getPageOverlays = useCallback((): ChunkOverlay[] => {
    const overlays: ChunkOverlay[] = [];

    for (const chunk of document.chunks) {
      if (!chunk.bounding_regions) continue;

      for (const region of chunk.bounding_regions) {
        if (region.page_number === currentPage) {
          overlays.push({
            chunk,
            pageNumber: region.page_number,
            polygon: region.polygon,
          });
        }
      }
    }

    return overlays;
  }, [document.chunks, currentPage]);

  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfError(null);
  };

  const onDocumentLoadError = (error: Error) => {
    setPdfError(`Failed to load PDF: ${error.message}`);
  };

  const onPageLoadSuccess = (page: { width: number; height: number }) => {
    setPageSize({ width: page.width, height: page.height });
  };

  // Convert normalized polygon coordinates to pixel coordinates
  const polygonToSvgPath = (polygon: number[], width: number, height: number): string => {
    if (polygon.length < 4) return '';

    const points: string[] = [];
    for (let i = 0; i < polygon.length; i += 2) {
      const x = polygon[i] * width;
      const y = polygon[i + 1] * height;
      points.push(`${x},${y}`);
    }

    return `M ${points.join(' L ')} Z`;
  };

  const handleOverlayClick = (chunk: DocumentChunk) => {
    setSelectedChunk(chunk);
    // Scroll to chunk in the list
    const chunkElement = window.document.getElementById(`chunk-${chunk.id}`);
    if (chunkElement) {
      chunkElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleChunkListClick = (chunk: DocumentChunk) => {
    setSelectedChunk(chunk);
    // Navigate to the page containing this chunk
    if (chunk.bounding_regions && chunk.bounding_regions.length > 0) {
      setCurrentPage(chunk.bounding_regions[0].page_number);
    } else if (chunk.start_page) {
      setCurrentPage(chunk.start_page);
    }
  };

  const pageOverlays = getPageOverlays();

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>{document.filename}</h2>
            <span className={styles.subtitle}>{document.ftp_path}</span>
          </div>
          <div className={styles.headerRight}>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>

        <div className={styles.body}>
          {/* PDF Viewer Panel */}
          <div className={styles.pdfPanel}>
            {/* PDF Controls */}
            <div className={styles.pdfControls}>
              <div className={styles.pageControls}>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  Prev
                </Button>
                <span className={styles.pageInfo}>
                  Page {currentPage} of {numPages || '?'}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= numPages}
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
              <div className={styles.zoomControls}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScale((s) => Math.max(0.5, s - 0.25))}
                >
                  -
                </Button>
                <span className={styles.zoomInfo}>{Math.round(scale * 100)}%</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setScale((s) => Math.min(2, s + 0.25))}
                >
                  +
                </Button>
              </div>
              <label className={styles.overlayToggle}>
                <input
                  type="checkbox"
                  checked={showOverlays}
                  onChange={(e) => setShowOverlays(e.target.checked)}
                />
                Show Overlays
              </label>
            </div>

            {/* PDF Container */}
            <div className={styles.pdfContainer} ref={containerRef}>
              {pdfError ? (
                <div className={styles.pdfError}>
                  <p>{pdfError}</p>
                  <p className={styles.pdfErrorHint}>
                    The PDF may not be accessible or the document is still processing.
                  </p>
                </div>
              ) : pdfLoading ? (
                <div className={styles.pdfLoading}>Loading PDF...</div>
              ) : pdfBlobUrl ? (
                <div className={styles.pdfWrapper}>
                  <Document
                    file={pdfBlobUrl}
                    onLoadSuccess={onDocumentLoadSuccess}
                    onLoadError={onDocumentLoadError}
                    loading={<div className={styles.pdfLoading}>Loading PDF...</div>}
                  >
                    <div className={styles.pageWrapper}>
                      <Page
                        pageNumber={currentPage}
                        scale={scale}
                        onLoadSuccess={onPageLoadSuccess}
                        renderTextLayer={true}
                        renderAnnotationLayer={true}
                      />

                      {/* SVG Overlay for bounding boxes */}
                      {showOverlays && pageSize && (
                        <svg
                          className={styles.overlayCanvas}
                          style={{
                            width: pageSize.width * scale,
                            height: pageSize.height * scale,
                          }}
                          viewBox={`0 0 ${pageSize.width * scale} ${pageSize.height * scale}`}
                        >
                          {pageOverlays.map((overlay, idx) => {
                            const isSelected = selectedChunk?.id === overlay.chunk.id;
                            const isHovered = hoveredChunk === overlay.chunk.id;
                            const isUseless = overlay.chunk.is_useless;

                            return (
                              <path
                                key={`${overlay.chunk.id}-${idx}`}
                                d={polygonToSvgPath(
                                  overlay.polygon,
                                  pageSize.width * scale,
                                  pageSize.height * scale
                                )}
                                className={`${styles.overlayPath} ${
                                  isSelected ? styles.selected : ''
                                } ${isHovered ? styles.hovered : ''} ${
                                  isUseless ? styles.useless : ''
                                }`}
                                onClick={() => handleOverlayClick(overlay.chunk)}
                                onMouseEnter={() => setHoveredChunk(overlay.chunk.id)}
                                onMouseLeave={() => setHoveredChunk(null)}
                              />
                            );
                          })}
                        </svg>
                      )}
                    </div>
                  </Document>
                </div>
              ) : null}
            </div>
          </div>

          {/* Chunks Panel */}
          <div className={styles.chunksPanel}>
            <div className={styles.chunksPanelHeader}>
              <h3>Chunks ({document.chunks.length})</h3>
              <div className={styles.chunkStats}>
                <span>{document.chunk_summary.with_bounding_box} with bounds</span>
                <span>{document.chunk_summary.useless} useless</span>
              </div>
            </div>

            <div className={styles.chunksList}>
              {document.chunks.map((chunk) => {
                const isExpanded = expandedChunks.has(chunk.id);
                const viewMode = chunkViewMode[chunk.id] || 'summary';
                const boundsCount = chunk.bounding_regions?.length || 0;

                return (
                  <div
                    key={chunk.id}
                    id={`chunk-${chunk.id}`}
                    className={`${styles.chunkItem} ${
                      selectedChunk?.id === chunk.id ? styles.selectedChunk : ''
                    } ${hoveredChunk === chunk.id ? styles.hoveredChunk : ''} ${
                      isExpanded ? styles.expandedChunk : ''
                    }`}
                    onClick={() => handleChunkListClick(chunk)}
                    onMouseEnter={() => setHoveredChunk(chunk.id)}
                    onMouseLeave={() => setHoveredChunk(null)}
                  >
                    {/* Compact Header */}
                    <div className={styles.chunkHeader}>
                      <div className={styles.chunkHeaderLeft}>
                        <span className={styles.chunkIndex}>#{chunk.chunk_index}</span>
                        <span className={styles.chunkMeta}>
                          {chunk.content_type} | {chunk.token_count} tok
                        </span>
                        {boundsCount > 0 && (
                          <span className={styles.pageTag}>
                            p{chunk.bounding_regions![0].page_number}
                            {boundsCount > 1 && ` (${boundsCount})`}
                          </span>
                        )}
                      </div>
                      <div className={styles.chunkHeaderRight}>
                        {chunk.is_useless && (
                          <StatusBadge variant="warning">Skip</StatusBadge>
                        )}
                        <button
                          className={styles.expandButton}
                          onClick={(e) => toggleChunkExpanded(chunk.id, e)}
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? '−' : '+'}
                        </button>
                      </div>
                    </div>

                    {/* Section Context */}
                    {chunk.heading_context && (
                      <div className={styles.chunkHeading} title={chunk.heading_context}>
                        {chunk.heading_context.length > 60
                          ? chunk.heading_context.substring(0, 60) + '...'
                          : chunk.heading_context}
                      </div>
                    )}

                    {/* Collapsed View: Show full summary or text */}
                    {!isExpanded && (
                      <div className={styles.chunkPreview}>
                        {chunk.summary || chunk.chunk_text}
                      </div>
                    )}

                    {/* Expanded View: Tabs + Full Content */}
                    {isExpanded && (
                      <div className={styles.chunkExpanded}>
                        {/* View Mode Tabs */}
                        <div className={styles.chunkTabs}>
                          <button
                            className={`${styles.chunkTab} ${viewMode === 'summary' ? styles.activeTab : ''}`}
                            onClick={(e) => setChunkView(chunk.id, 'summary', e)}
                          >
                            Summary
                          </button>
                          <button
                            className={`${styles.chunkTab} ${viewMode === 'raw' ? styles.activeTab : ''}`}
                            onClick={(e) => setChunkView(chunk.id, 'raw', e)}
                          >
                            Raw Text
                          </button>
                        </div>

                        {/* Content Area */}
                        <div className={styles.chunkContent}>
                          {viewMode === 'summary' ? (
                            <div className={styles.chunkSummaryFull}>
                              {chunk.summary || <span className={styles.noSummary}>No summary available</span>}
                            </div>
                          ) : (
                            <pre className={styles.chunkRawText}>{chunk.chunk_text}</pre>
                          )}
                        </div>

                        {/* Metadata Footer */}
                        <div className={styles.chunkFooter}>
                          <span>Tokens: {chunk.token_count}</span>
                          {chunk.summary_tokens > 0 && <span>Summary: {chunk.summary_tokens} tok</span>}
                          <span>Bounds: {boundsCount}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
